/**
 * The theme's fonts, made into fonts the engine can embed.
 *
 * A host declares a font once, in `theme.fonts`. The browser has always read that
 * declaration — `@font-face` rules on the canvas, the designer's font pickers, the
 * validator, the Chromium backend — and this engine did not: it took a separate
 * `fonts` option, so a font declared only in the theme was drawn on screen and
 * measured and printed in the standard faces. Nothing said so, and the symptom was
 * a canvas whose sections overlapped and a PDF whose lines broke somewhere else.
 *
 * Three things stand between a `@font-face` declaration and the engine.
 *
 * **Format.** The engine parses TrueType and OpenType and nothing else; there is no
 * WOFF decoder in it. A face often lists a WOFF2 for the browser beside a TTF, so
 * the first source the engine can read is the one it gets, and a WOFF is skipped by
 * its name before anything is downloaded. What is downloaded is checked by its
 * first four bytes, not its extension. A face with nothing readable is a warning,
 * because the alternative is exactly the silence described above.
 *
 * **Where it comes from.** A theme is the host's own code in the SDK and a JSON body
 * from a stranger in the render service, so the service narrows `FontSourcePolicy`
 * to its allowed hosts and to no files at all. The SDK default is to trust the
 * theme, as it trusts the template.
 *
 * **Doing it once.** The engine resolves a font by rewriting its entry in place,
 * and re-reads anything that is not a `data:` URI on every render. Every source is
 * therefore resolved here, once per address, to a `data:` URI the engine passes
 * straight through.
 */
import type { Theme, ThemeFontFace } from "@broadpaper/core";

export interface FormeFont {
  family: string;
  /** TTF/OTF bytes, a data: URI, or a filesystem path (Node only). */
  src: string | Uint8Array;
  weight?: number;
  style?: "normal" | "italic";
}

/** Where the theme's fonts may be read from. */
export interface FontSourcePolicy {
  /** Hosts an `http(s)` font may be fetched from; `"*"` for any. Unset means any. */
  allowedHosts?: string[];
  /**
   * Whether a source that is neither `data:` nor `http(s)` may be read — a file in
   * Node, an address relative to the page in a browser. Default true.
   */
  local?: boolean;
}

/** Reads a source that is neither `data:` nor `http(s)`. Installed by each entry point. */
export type FontFileReader = (src: string) => Promise<Uint8Array>;

let localReader: FontFileReader | null = null;

/**
 * Installs the reader for local sources. `src/index.ts` reads files and
 * `src/index.browser.ts` fetches relative to the page, and neither bundle contains
 * the other's — the same arrangement as `setEngineLoader`.
 */
export function setFontFileReader(reader: FontFileReader): void {
  localReader = reader;
}

export interface ResolvedFonts {
  fonts: FormeFont[];
  warnings: string[];
}

/** Larger than any single face in use, CJK included; a guard against a runaway response, not a style rule. */
const MAX_FONT_BYTES = 32 * 1024 * 1024;
const FETCH_TIMEOUT_MS = 15_000;
/** Addresses whose resolution is kept. A long-running service meets a bounded set of brands. */
const MAX_CACHED = 64;

type Loaded = { src: string } | { reason: string };

const cache = new Map<string, Promise<Loaded>>();

/**
 * The fonts to hand the engine: the caller's own `fonts` first, exactly as given,
 * then every face in the theme the engine can read.
 *
 * The first entry for a family, weight and style wins, so an explicit font — or a
 * service's own — is never displaced by a theme naming the same face.
 */
export async function resolveFonts(input: { fonts?: FormeFont[]; theme?: Partial<Theme>; sources?: FontSourcePolicy }): Promise<ResolvedFonts> {
  const fonts: FormeFont[] = [];
  const warnings: string[] = [];
  const seen = new Set<string>();
  const add = (font: FormeFont) => {
    const weight = font.weight ?? 400;
    const style = font.style ?? "normal";
    const key = `${font.family.trim().toLowerCase()}|${weight}|${style}`;
    if (seen.has(key)) return;
    seen.add(key);
    fonts.push({ family: font.family, src: font.src, weight, style });
  };

  for (const font of input.fonts ?? []) add(font);
  const faces = await Promise.all((input.theme?.fonts ?? []).map((face) => themeFace(face, input.sources ?? {})));
  for (const face of faces) {
    warnings.push(...face.warnings);
    if (face.font) add(face.font);
  }
  return { fonts, warnings };
}

async function themeFace(face: ThemeFontFace, policy: FontSourcePolicy): Promise<{ font?: FormeFont; warnings: string[] }> {
  const { weight, range } = weightOf(face.weight);
  const style = face.style === "italic" ? "italic" : "normal";
  const name = `"${face.family}" (${weight}${style === "italic" ? " italic" : ""})`;
  const sources = (Array.isArray(face.src) ? face.src : [face.src]).filter((s): s is string => typeof s === "string" && s.trim() !== "");

  const reasons: string[] = [];
  for (const src of sources) {
    const loaded = await load(src.trim(), policy);
    if ("reason" in loaded) {
      reasons.push(loaded.reason);
      continue;
    }
    const warnings = range
      ? [`The font "${face.family}" declares the weights ${String(face.weight)} from one file; the PDF engine is given it at ${weight} only. Declare a file per weight for the others to be embedded.`]
      : [];
    return { font: { family: face.family, src: loaded.src, weight, style }, warnings };
  }
  return {
    warnings: [
      `The font ${name} has no TrueType or OpenType source the PDF engine can read, so the PDF draws that text in a standard face and breaks its lines differently from the screen. ` +
        `Add a .ttf or .otf to its src${reasons.length ? ` (${reasons.join("; ")})` : ""}.`
    ]
  };
}

/** A CSS font-weight, as the single number the engine takes. A range is a variable font. */
function weightOf(value: ThemeFontFace["weight"]): { weight: number; range: boolean } {
  if (typeof value === "number" && Number.isFinite(value)) return { weight: Math.round(value), range: false };
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "bold") return { weight: 700, range: false };
  const numbers = text.split(/\s+/).map(Number).filter((n) => Number.isFinite(n) && n > 0);
  if (!numbers.length) return { weight: 400, range: false };
  return { weight: Math.round(numbers[0]!), range: numbers.length > 1 && numbers[1] !== numbers[0] };
}

async function load(src: string, policy: FontSourcePolicy): Promise<Loaded> {
  // By name, before anything is fetched: a face's WOFF2 is for the browser beside it.
  if (/^data:(font|application)\/(x-)?woff2?[;,]/i.test(src) || /\.woff2?([?#]|$)/i.test(src.startsWith("data:") ? "" : src)) {
    return { reason: `${describe(src)} is WOFF, which only a browser reads` };
  }

  if (src.startsWith("data:")) return fromDataUri(src);

  const remote = /^https?:\/\//i.test(src);
  if (remote) {
    let host: string;
    try {
      host = new URL(src).hostname;
    } catch {
      return { reason: `${describe(src)} is not a valid address` };
    }
    const allowed = policy.allowedHosts;
    if (allowed && !allowed.includes("*") && !allowed.includes(host)) return { reason: `${host} is not an allowed host` };
  } else if (policy.local === false) {
    return { reason: `${describe(src)} is neither a data: URI nor a web address, and files are not read here` };
  } else if (!localReader) {
    // No entry point installed a reader — a caller importing the module directly.
    // The engine resolves such a source itself, which is what it did before.
    return { src };
  }

  const key = src;
  const hit = cache.get(key);
  if (hit) return hit;
  const pending = (async (): Promise<Loaded> => {
    const bytes = remote ? await download(src, Boolean(policy.allowedHosts && !policy.allowedHosts.includes("*"))) : await localReader!(src);
    if (bytes.byteLength > MAX_FONT_BYTES) return { reason: `${describe(src)} is larger than ${MAX_FONT_BYTES / 1024 / 1024} MB` };
    const problem = notEmbeddable(bytes, src);
    return problem ? { reason: problem } : { src: `data:font/ttf;base64,${toBase64(bytes)}` };
  })().catch((e: unknown) => {
    // A failure is not kept: the next render tries again rather than failing for the
    // life of the process.
    cache.delete(key);
    return { reason: `${describe(src)} could not be read (${(e as Error).message || String(e)})` };
  });
  cache.set(key, pending);
  while (cache.size > MAX_CACHED) cache.delete(cache.keys().next().value!);
  return pending;
}

async function download(src: string, restricted: boolean): Promise<Uint8Array> {
  const signal = typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(FETCH_TIMEOUT_MS) : undefined;
  // A redirect would take a restricted fetch to a host nobody checked.
  const response = await fetch(src, { redirect: restricted ? "error" : "follow", ...(signal ? { signal } : {}) });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  const length = Number(response.headers.get("content-length"));
  if (length > MAX_FONT_BYTES) throw new Error(`larger than ${MAX_FONT_BYTES / 1024 / 1024} MB`);
  return new Uint8Array(await response.arrayBuffer());
}

function fromDataUri(src: string): Loaded {
  const match = /^data:([^,]*),(.*)$/s.exec(src);
  if (!match) return { reason: "a data: URI with no data" };
  const base64 = /;base64$/i.test(match[1]!);
  let head: Uint8Array;
  try {
    head = base64 ? fromBase64(match[2]!.slice(0, 8)) : Uint8Array.from(decodeURIComponent(match[2]!.slice(0, 64)), (c) => c.charCodeAt(0) & 0xff);
  } catch {
    return { reason: "a data: URI that does not decode" };
  }
  const problem = notEmbeddable(head, "a data: URI");
  if (problem) return { reason: problem };
  if (base64) return { src };
  // The engine only takes the base64 form.
  const bytes = Uint8Array.from(decodeURIComponent(match[2]!), (c) => c.charCodeAt(0) & 0xff);
  return { src: `data:font/ttf;base64,${toBase64(bytes)}` };
}

/** Null when these are TrueType or OpenType bytes, otherwise why not. */
function notEmbeddable(bytes: Uint8Array, src: string): string | null {
  if (bytes.length < 4) return `${describe(src)} is not a font file`;
  const tag = String.fromCharCode(bytes[0]!, bytes[1]!, bytes[2]!, bytes[3]!);
  if ((bytes[0] === 0 && bytes[1] === 1 && bytes[2] === 0 && bytes[3] === 0) || tag === "OTTO" || tag === "true") return null;
  if (tag === "wOFF" || tag === "wOF2") return `${describe(src)} is WOFF, which only a browser reads`;
  if (tag === "ttcf") return `${describe(src)} is a font collection; give the single face instead`;
  return `${describe(src)} is not a TrueType or OpenType file`;
}

function describe(src: string): string {
  return src.startsWith("data:") ? "a data: URI" : src.length > 80 ? `${src.slice(0, 77)}…` : src;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

function fromBase64(text: string): Uint8Array {
  const clean = text.replace(/[^A-Za-z0-9+/]/g, "");
  const binary = atob(clean.slice(0, clean.length - (clean.length % 4)));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}
